/* Accès aux données du CRM : tables Supabase, stockage, fonctions et modèles de messages. */
import { supabase } from '../../lib/supabase';
import type { AccountState, CrmContact, CrmContactDraft, Directory, MessageTemplate, Meter, Role, SharingOperation, TemplateKey } from './types';
import { defaultAnnualFee, displayName, formatBce, normalizeMeters, signatoryName } from './types';
import { defaultVatRate, fromMwh, toMwh } from './participants';
import type { RequirementOverrides } from './validation';
import { errorMessage } from './ui';
import { crmSessionProblem, noRowError } from './session';
import {
  type SavingsEstimate, contractEstimate, fetchSavingsSettings, renderSavingsSentence, savingsVariables,
} from './savingsEstimate';
import {
  type TodoContact, type TodoDone, type TodoParticipant, type TodoSettings, currentMonth,
} from '../../../supabase/functions/_shared/crmTodo';

const TABLE = 'crm_contacts';
const DOCS_BUCKET = 'crm-documents';
const TEMPLATES_BUCKET = 'contract-templates';

const toContact = (row: Record<string, unknown>): CrmContact => ({
  ...(row as unknown as CrmContact),
  meters: normalizeMeters(row.meters),
  sibelga_status: Array.isArray(row.sibelga_status) ? (row.sibelga_status as CrmContact['sibelga_status']) : [],
});

/* ── Contacts ── */

export async function fetchContacts(includeArchived: boolean): Promise<CrmContact[]> {
  let query = supabase.from(TABLE).select('*').order('updated_at', { ascending: false });
  if (!includeArchived) query = query.is('archived_at', null);
  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []).map((r: Record<string, unknown>) => toContact(r));
}

export async function createContact(draft: CrmContactDraft): Promise<CrmContact> {
  const { data, error } = await supabase.from(TABLE).insert({ ...draft, stage: 'lead' }).select().single();
  if (error) {
    // Sans session administrateur, la base refuse la création (règles d'accès) : message explicite.
    const problem = await crmSessionProblem().catch(() => null);
    throw problem ? new Error(problem) : error;
  }
  return toContact(data as Record<string, unknown>);
}

export async function updateContact(id: string, patch: CrmContactDraft): Promise<CrmContact> {
  const { data, error } = await supabase.from(TABLE).update(patch).eq('id', id).select().maybeSingle();
  if (error) throw error;
  // Aucune ligne modifiée : session perdue (cas le plus fréquent) ou fiche disparue.
  if (!data) throw await noRowError(TABLE, id, 'Fiche');
  return toContact(data as Record<string, unknown>);
}

export async function archiveContact(id: string, archived: boolean): Promise<CrmContact> {
  return updateContact(id, { archived_at: archived ? new Date().toISOString() : null });
}

/* ── Données liées ── */

export async function fetchOperations(): Promise<SharingOperation[]> {
  const { data, error } = await supabase
    .from('sharing_operations')
    .select('id, name')
    .is('deleted_at', null)
    .order('name');
  if (error) throw error;
  return (data ?? []) as SharingOperation[];
}

/**
 * Voyant « Compte », même règle que le tableau de bord admin : un compte est activé quand la fiche participant
 * a un e-mail de connexion (participants.email). « pending » si une demande de compte attend la validation,
 * sinon le statut de la fiche. Pour un membre à plusieurs EAN (groupe), le meilleur état l'emporte.
 */
export async function fetchAccountStatuses(contacts: CrmContact[]): Promise<Record<string, AccountState>> {
  const byParticipant = new Map<string, string>();
  for (const c of contacts) {
    for (const m of c.meters) if (m.participant_id) byParticipant.set(m.participant_id, c.id);
  }
  const ids = Array.from(byParticipant.keys());
  const result: Record<string, AccountState> = {};
  if (ids.length === 0) return result;

  const mark = (participantId: string, state: AccountState) => {
    const contactId = byParticipant.get(participantId);
    if (!contactId) return;
    if (state === 'approved' || !result[contactId]) result[contactId] = state;
  };
  // Requêtes par paquets pour ne pas dépasser la longueur d'URL.
  for (let i = 0; i < ids.length; i += 150) {
    const chunk = ids.slice(i, i + 150);
    const [{ data: accounts }, { data: participants }] = await Promise.all([
      supabase.from('participant_accounts').select('participant_id, account_status').in('participant_id', chunk),
      supabase.from('participants').select('id, email, account_status').in('id', chunk),
    ]);
    const pending = new Set(((accounts ?? []) as { participant_id: string; account_status: string | null }[])
      .filter(a => a.account_status === 'pending').map(a => a.participant_id));
    for (const row of (participants ?? []) as { id: string; email: string | null; account_status: string | null }[]) {
      if (!row.email?.trim()) continue; // pas d'accès plateforme créé
      if (pending.has(row.id)) mark(row.id, 'pending');
      else mark(row.id, row.account_status === 'approved' ? 'approved' : 'pending');
    }
  }
  return result;
}

/** Groupes, dénominations et n° BCE existants, pour les suggestions de saisie. */
export async function fetchDirectory(): Promise<Directory> {
  const [{ data: participants }, { data: contacts }] = await Promise.all([
    supabase.from('participants').select('groupe, company_name, company_number'),
    supabase.from(TABLE).select('groupe').not('groupe', 'is', null),
  ]);
  const groupCounts = new Map<string, number>();
  const companyNames = new Set<string>();
  const companyNumberToName: Record<string, string> = {};
  for (const p of (participants ?? []) as { groupe: string | null; company_name: string | null; company_number: string | null }[]) {
    const g = p.groupe?.trim();
    if (g) groupCounts.set(g, (groupCounts.get(g) ?? 0) + 1);
    const name = p.company_name?.trim();
    if (name) companyNames.add(name);
    const num = formatBce(p.company_number);
    if (num && name && !(num in companyNumberToName)) companyNumberToName[num] = name;
  }
  for (const c of (contacts ?? []) as { groupe: string | null }[]) {
    const g = c.groupe?.trim();
    if (g && !groupCounts.has(g)) groupCounts.set(g, 0);
  }
  return {
    groups: Array.from(groupCounts, ([name, eanCount]) => ({ name, eanCount })).sort((a, b) => a.name.localeCompare(b.name)),
    companyNames: Array.from(companyNames).sort((a, b) => a.localeCompare(b)),
    companyNumberToName,
  };
}

/**
 * Groupe dans lequel les EAN du membre seront rangés au passage en « Membre actif » :
 * le groupe indiqué dans la fiche, sinon un groupe à son nom s'il a plusieurs EAN.
 */
export function targetGroupFor(c: Pick<CrmContact, 'groupe' | 'meters' | 'entity_type' | 'company_name' | 'first_name' | 'last_name' | 'email'>): string | null {
  const g = c.groupe?.trim();
  if (g) return g;
  return c.meters.length > 1 ? signatoryName(c) : null;
}

/** Carte « Membre actif » d'un groupe existant (même nom de groupe, sans tenir compte des majuscules). */
export async function findGroupCard(groupe: string, excludeId: string): Promise<CrmContact | null> {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .eq('stage', 'active')
    .is('archived_at', null)
    .ilike('groupe', groupe.replace(/[%_]/g, m => `\\${m}`))
    .neq('id', excludeId)
    .limit(1);
  if (error) throw error;
  const row = (data ?? [])[0] as Record<string, unknown> | undefined;
  return row ? toContact(row) : null;
}

/** Prix pratiqués dans un groupe, en c€/kWh HTVA. */
export interface GroupPrice {
  price: number | null;
  /** Prosumer uniquement */
  sellPrice: number | null;
}

/** Valeur la plus fréquente (les prix d'un groupe sont en principe identiques). */
function mostFrequent(values: (number | null | undefined)[]): number | null {
  const counts = new Map<number, number>();
  for (const v of values) if (v !== null && v !== undefined && Number(v) > 0) counts.set(Number(v), (counts.get(Number(v)) ?? 0) + 1);
  let best: number | null = null;
  let bestCount = 0;
  for (const [v, n] of counts) if (n > bestCount) { best = v; bestCount = n; }
  return best;
}


/**
 * Prix du groupe pour un nouveau membre de même rôle : prix des fiches participants du groupe
 * (€/MWh → c€/kWh), sinon prix de la carte du groupe. null si le groupe n'a pas de prix pour ce rôle.
 */
export async function fetchGroupPrice(groupe: string, role: Role | null, excludeId: string | null): Promise<GroupPrice | null> {
  const r: Role = role ?? 'consumer';
  const { data, error } = await supabase
    .from('participants')
    .select('type, shared_energy_price, prosumer_buy_price, prosumer_sell_price')
    .ilike('groupe', groupe.trim().replace(/[%_]/g, m => `\\${m}`));
  if (error) throw error;
  type Row = { type: string | null; shared_energy_price: number | null; prosumer_buy_price: number | null; prosumer_sell_price: number | null };
  const rows = ((data ?? []) as Row[]).filter(p => (p.type ?? 'consumer') === r);
  let price = fromMwh(mostFrequent(rows.map(p => (r === 'prosumer' ? p.prosumer_buy_price : p.shared_energy_price))));
  let sellPrice = r === 'prosumer' ? fromMwh(mostFrequent(rows.map(p => p.prosumer_sell_price))) : null;

  if (price === null) {
    const card = await findGroupCard(groupe, excludeId ?? '');
    if (card && (card.role ?? 'consumer') === r) {
      price = card.sharing_price_cents_kwh ?? card.price_cents_kwh;
      if (r === 'prosumer') sellPrice = card.sharing_sell_price_cents_kwh ?? card.sell_price_cents_kwh;
    }
  }
  return price === null && sellPrice === null ? null : { price, sellPrice };
}

/**
 * Crée une fiche participants par point de comptage qui n'en a pas encore.
 * Mêmes valeurs par défaut que ParticipantForm.tsx ; prix : c€/kWh × 10 = €/MWh ;
 * n° BCE au format « BE 0123.456.789 ». Renvoie les compteurs avec leur participant_id.
 */
export async function createParticipantsForContact(c: CrmContact, groupe: string | null): Promise<Meter[]> {
  const name = signatoryName(c);
  const several = c.meters.length > 1;
  const role: Role = c.role ?? 'consumer';
  const buy = c.sharing_price_cents_kwh ?? c.price_cents_kwh;
  const sell = c.sharing_sell_price_cents_kwh ?? c.sell_price_cents_kwh;
  const updated: Meter[] = [];
  let createdAny = false;

  for (const [i, m] of c.meters.entries()) {
    if (m.participant_id) {
      updated.push(m);
      continue;
    }
    // Nom saisi pour cet EAN (obligatoire pour passer en « Membre actif », comme dans le formulaire admin).
    const fallback = several ? `${name} - ${m.meter_address || `compteur ${i + 1}`}` : name;
    const payload: Record<string, unknown> = {
      name: m.name?.trim() || fallback,
      address: m.meter_address ?? '',
      type: role,
      send_email: (c.billing_email || c.email)?.toLowerCase() ?? null,
      ean_code: m.ean_code,
      entry_date: c.sharing_start_date,
      company_number: c.entity_type === 'morale' ? formatBce(c.company_number) : null,
      company_name: c.entity_type === 'morale' ? c.company_name : null,
      groupe,
      lat: 50.8503,
      lng: 4.3517,
      annual_consumption: 0,
      contract_formula_type: null, // contrat fournisseur : non renseigné par défaut (onglet Contrat du membre)
      tariff_type: 'mono',
      sharing_operation_id: c.sharing_operation_id,
      shared_energy_formula_type: 'fixed',
      reimbursement_period: role === 'consumer' ? null : c.reimbursement_period ?? 'quarterly',
      // Comme dans le formulaire admin, un prosumer garde aussi son prix d'achat dans shared_energy_price.
      shared_energy_price: toMwh(buy),
      adhesion_fee: c.annual_fee ?? defaultAnnualFee(c.entity_type),
      prosumer_buy_price: role === 'prosumer' ? toMwh(buy) : null,
      prosumer_sell_price: role === 'prosumer' ? toMwh(sell) : null,
      vat_rate: c.sharing_vat_rate ?? defaultVatRate(c),
      // Valeurs par défaut d'un nouveau participant dans le formulaire admin.
      show_economic_tab: false,
      show_qh_data: true,
    };
    const { data, error } = await supabase.from('participants').insert(payload).select('id').single();
    if (error) {
      // Enregistre les fiches déjà créées avant de signaler l'erreur, pour ne pas les recréer.
      if (createdAny) await updateContact(c.id, { meters: [...updated, ...c.meters.slice(updated.length)] });
      throw new Error(`Fiche participant pour l'EAN ${m.ean_code} : ${error.message}`);
    }
    createdAny = true;
    updated.push({ ...m, participant_id: (data as { id: string }).id });
  }
  return updated;
}

/**
 * Un groupe a un seul contact (celui de sa carte, modifiable dans la fiche du groupe).
 * Si la carte n'a pas encore d'e-mail, la société qui rejoint le groupe en devient le contact.
 */
export function groupContactPatch(card: CrmContact, c: CrmContact): CrmContactDraft {
  if (card.email?.trim() || !c.email?.trim()) return {};
  return { email: c.email.trim(), ...(!card.phone?.trim() && c.phone?.trim() ? { phone: c.phone.trim() } : {}) };
}

/**
 * Rattache une société à la carte d'un groupe existant : ses EAN rejoignent la carte du groupe,
 * et sa propre fiche (contrat, signature) est conservée dans les archivés avec un lien vers le groupe.
 */
export async function mergeIntoGroupCard(
  c: CrmContact,
  card: CrmContact,
  meters: Meter[],
  patch: CrmContactDraft,
): Promise<{ card: CrmContact; merged: CrmContact }> {
  const known = new Set(card.meters.map(m => m.ean_code));
  const updatedCard = await updateContact(card.id, {
    meters: [...card.meters, ...meters.filter(m => !known.has(m.ean_code))],
    ...groupContactPatch(card, c),
  });
  const today = new Date().toLocaleDateString('fr-BE');
  const note = `Rattaché au groupe « ${card.groupe} » le ${today}.`;
  const merged = await updateContact(c.id, {
    ...patch,
    meters,
    groupe: card.groupe,
    merged_into_id: card.id,
    archived_at: new Date().toISOString(),
    notes: c.notes ? `${c.notes}\n${note}` : note,
  });
  return { card: updatedCard, merged };
}

/* ── Paramètres du CRM ── */

/** Étape à partir de laquelle chaque champ est exigé (réglages de CRM → Paramètres). Vide = valeurs par défaut. */
export async function fetchFieldRequirements(): Promise<RequirementOverrides> {
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'field_requirements').maybeSingle();
  if (error) throw error;
  const value = (data as { value?: unknown } | null)?.value;
  return value && typeof value === 'object' ? (value as RequirementOverrides) : {};
}

export async function saveFieldRequirements(value: RequirementOverrides): Promise<void> {
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'field_requirements', value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

/* ── Todolist (onglet Communications) et récap mensuel ── */

const EMPTY_TODO_SETTINGS: TodoSettings = { operations: {}, reportsFrom: currentMonth() };

export async function fetchTodoSettings(): Promise<TodoSettings> {
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'todo').maybeSingle();
  if (error) throw labelled('Réglages de la todolist (crm_settings)', error);
  const value = (data as { value?: Partial<TodoSettings> } | null)?.value;
  return { operations: value?.operations ?? {}, reportsFrom: value?.reportsFrom ?? EMPTY_TODO_SETTINGS.reportsFrom };
}

export async function saveTodoSettings(value: TodoSettings): Promise<void> {
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'todo', value, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

/** Erreur Supabase précédée de la donnée concernée (sans « [object Object] »). */
const labelled = (label: string, err: unknown): Error => new Error(`${label} : ${errorMessage(err)}`);

/** Fiches participants et mois pour lesquels chaque EAN a des données. */
export async function fetchTodoParticipants(): Promise<TodoParticipant[]> {
  const { data: rows, error } = await supabase.from('participants')
    .select('id, name, type, groupe, company_number, company_name, entry_date, sharing_operation_id, reimbursement_period, email');
  if (error) throw labelled('Fiches participants', error);

  // Mois disponibles : fonction SQL crm_participant_months, sinon lecture des clés de monthly_data.
  let months: { participant_id: string; months: string[] }[] | null = null;
  const rpc = await supabase.rpc('crm_participant_months');
  if (!rpc.error) {
    months = (rpc.data ?? []) as { participant_id: string; months: string[] }[];
  } else {
    console.warn('crm_participant_months indisponible, lecture de monthly_data :', errorMessage(rpc.error));
    const { data: raw, error: rawError } = await supabase.from('participants').select('id, monthly_data');
    if (rawError) throw labelled('Données mensuelles', rawError);
    months = ((raw ?? []) as { id: string; monthly_data: unknown }[]).map(r => ({
      participant_id: r.id,
      months: r.monthly_data && typeof r.monthly_data === 'object' ? Object.keys(r.monthly_data as object).sort() : [],
    }));
  }
  const byId = new Map<string, string[]>(months.map(m => [m.participant_id, m.months ?? []]));
  return ((rows ?? []) as (Omit<TodoParticipant, 'months' | 'has_account'> & { email: string | null })[]).map(({ email, ...p }) => ({
    ...p, has_account: !!email?.trim(), months: byId.get(p.id) ?? [],
  }));
}

export async function fetchTodoDone(): Promise<TodoDone[]> {
  const { data, error } = await supabase.from('crm_todo_done').select('kind, ref, period');
  if (error) throw labelled('Tâches cochées (crm_todo_done)', error);
  return (data ?? []) as TodoDone[];
}

export async function setTodoDone(item: TodoDone, done: boolean): Promise<void> {
  if (done) {
    const { data: session } = await supabase.auth.getSession();
    const { error } = await supabase.from('crm_todo_done')
      .upsert({ ...item, done_at: new Date().toISOString(), done_by: session.session?.user?.email ?? null }, { onConflict: 'kind,ref,period' });
    if (error) throw error;
  } else {
    const { error } = await supabase.from('crm_todo_done').delete().eq('kind', item.kind).eq('ref', item.ref).eq('period', item.period);
    if (error) throw error;
  }
}

/** Fiches CRM au format de la todolist. */
export function todoContacts(contacts: CrmContact[]): TodoContact[] {
  return contacts.map(c => ({
    id: c.id, name: displayName(c), email: c.email, stage: c.stage, groupe: c.groupe, merged_into_id: c.merged_into_id,
    archived_at: c.archived_at, sharing_operation_id: c.sharing_operation_id, sharing_start_date: c.sharing_start_date,
    onboarding_sent_at: c.onboarding_sent_at, participant_ids: c.meters.map(m => m.participant_id).filter((v): v is string => !!v),
    waiting_on: c.waiting_on, waiting_note: c.waiting_note, waiting_since: c.waiting_since,
    invitation_sent_at: c.invitation_sent_at,
    role: c.role, entity_type: c.entity_type, billing_channel: c.billing_channel, mandate_status: c.mandate_status,
  }));
}

/** Envoie tout de suite le récap du mois à info@sunisup.be. */
export async function sendDigestNow(): Promise<{ to: string; counts: Record<string, number> }> {
  return callFunction('crm-monthly-digest', { source: 'crm' });
}

/** Fiches des sociétés rattachées à la carte d'un groupe (contrat signé, dates). */
export async function fetchMergedContacts(cardId: string): Promise<CrmContact[]> {
  const { data, error } = await supabase.from(TABLE).select('*').eq('merged_into_id', cardId);
  if (error) throw error;
  return (data ?? []).map((r: Record<string, unknown>) => toContact(r));
}

/* ── Fichiers ── */

export async function uploadSignedMandate(contactId: string, file: File): Promise<string> {
  const path = `${contactId}/mandat-signe-${Date.now()}.pdf`;
  const { error } = await supabase.storage.from(DOCS_BUCKET).upload(path, file, { upsert: true, contentType: 'application/pdf' });
  if (error) throw error;
  return path;
}

export async function uploadSignedContract(contactId: string, file: File): Promise<string> {
  const path = `${contactId}/contrat-signe-${Date.now()}.pdf`;
  const { error } = await supabase.storage.from(DOCS_BUCKET).upload(path, file, { upsert: true, contentType: 'application/pdf' });
  if (error) throw error;
  return path;
}

export async function openStoredFile(bucket: 'crm-documents' | 'contract-templates', path: string): Promise<void> {
  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(path, 120);
  if (error || !data?.signedUrl) throw error ?? new Error('Lien indisponible');
  window.open(data.signedUrl, '_blank', 'noopener');
}

/** Fichiers du bucket des modèles de contrat : nom → date de mise à jour. */
export async function listContractTemplates(): Promise<Map<string, string | null>> {
  const { data, error } = await supabase.storage.from(TEMPLATES_BUCKET).list('');
  if (error) throw error;
  type StoredFile = { name: string; updated_at?: string | null; created_at?: string | null };
  return new Map<string, string | null>(
    ((data ?? []) as StoredFile[]).map(f => [f.name, f.updated_at ?? f.created_at ?? null]),
  );
}

export async function uploadContractTemplate(fileName: string, file: File): Promise<void> {
  const { error } = await supabase.storage.from(TEMPLATES_BUCKET).upload(fileName, file, {
    upsert: true,
    contentType: 'application/pdf',
  });
  if (error) throw error;
}

/** Coordonnées de la Communauté écrites dans les conventions (crm_settings, clé « contract »). */
export interface ContractSettings {
  communityBce: string;
  communityEmail: string;
}

export const DEFAULT_CONTRACT_SETTINGS: ContractSettings = { communityBce: '', communityEmail: 'info@sunisup.be' };

export async function fetchContractSettings(): Promise<ContractSettings> {
  const { data, error } = await supabase.from('crm_settings').select('value').eq('key', 'contract').maybeSingle();
  if (error) throw error;
  const v = (data as { value?: Partial<ContractSettings> } | null)?.value ?? {};
  return {
    communityBce: typeof v.communityBce === 'string' ? v.communityBce : DEFAULT_CONTRACT_SETTINGS.communityBce,
    communityEmail: typeof v.communityEmail === 'string' ? v.communityEmail : DEFAULT_CONTRACT_SETTINGS.communityEmail,
  };
}

export async function saveContractSettings(value: ContractSettings): Promise<void> {
  const bce = value.communityBce.replace(/\D/g, '');
  if (bce && bce.length !== 10) throw new Error('Le n° BCE compte 10 chiffres (ex. 1012.345.678)');
  const email = value.communityEmail.trim().toLowerCase();
  if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error('Adresse e-mail de la Communauté invalide');
  const clean: ContractSettings = { communityBce: bce ? `${bce.slice(0, 4)}.${bce.slice(4, 7)}.${bce.slice(7)}` : '', communityEmail: email };
  const { error } = await supabase.from('crm_settings')
    .upsert({ key: 'contract', value: clean, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

/* ── Fonctions Supabase ── */

export async function callFunction<T>(name: string, body: unknown): Promise<T> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error('Session expirée : reconnectez-vous');
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${name}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const payload = await res.json().catch(() => null) as (T & { error?: string }) | null;
  if (!res.ok) throw new Error(payload?.error ?? `Erreur ${res.status} (${name})`);
  return payload as T;
}

export interface GeneratedContract {
  pdfBase64: string;
  fileName: string;
  /** Modèle utilisé (ex. consommateur_pp.pdf). */
  template?: string;
  /** Points à corriger (N° BCE de la Communauté absent, ancien modèle utilisé). */
  warnings?: string[];
}

export interface GenerateOptions {
  /** Contrat avec mandat SEPA (particulier consommateur). */
  sepa?: boolean;
  /** Document : la convention (défaut) ou le mandat SEPA seul. */
  document?: 'contract' | 'mandate';
  /** Envoi : la base attribue la référence du mandat. Aperçu : non. */
  assignReference?: boolean;
}

export async function generateContract(contactId: string, opts: GenerateOptions = {}): Promise<GeneratedContract & { mandate_reference?: string | null }> {
  return callFunction('generate-contract', {
    contact_id: contactId,
    sepa: !!opts.sepa,
    document: opts.document ?? 'contract',
    assign_reference: !!opts.assignReference,
  });
}

export function downloadBase64Pdf(base64: string, fileName: string): void {
  const bytes = Uint8Array.from(atob(base64), ch => ch.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

interface Recipient {
  name: string;
  email: string;
  subject: string;
  message: string;
  attachmentBase64?: string;
  attachmentFilename?: string;
}

async function sendMemberEmail(recipient: Recipient): Promise<void> {
  const result = await callFunction<{ results?: { success: boolean; error?: string }[] }>('send-member-email', {
    recipients: [recipient],
  });
  const first = result.results?.[0];
  if (!first?.success) throw new Error(first?.error ?? "L'e-mail n'a pas pu être envoyé");
}

/* ── Modèles de messages (CRM → Paramètres) ── */

export async function fetchTemplates(): Promise<MessageTemplate[]> {
  const { data, error } = await supabase.from('crm_message_templates').select('*').order('label');
  if (error) throw error;
  return (data ?? []) as MessageTemplate[];
}

export async function saveTemplate(key: TemplateKey, subject: string, body: string): Promise<MessageTemplate> {
  const { data, error } = await supabase
    .from('crm_message_templates')
    .update({ subject, body, updated_at: new Date().toISOString() })
    .eq('key', key)
    .select()
    .maybeSingle();
  if (error) throw error;
  if (!data) throw new Error((await crmSessionProblem().catch(() => null)) ?? 'Modèle de message introuvable : actualisez le CRM');
  return data as MessageTemplate;
}

async function getTemplate(key: TemplateKey): Promise<MessageTemplate> {
  const { data, error } = await supabase.from('crm_message_templates').select('*').eq('key', key).maybeSingle();
  if (error) throw error;
  if (!data) throw new Error('Modèle de message introuvable : vérifiez CRM → Paramètres');
  return data as MessageTemplate;
}

const escapeHtml = (v: string) =>
  v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const fmtNumber = (v: number | null, decimals: number) =>
  v === null || v === undefined ? '' : Number(v).toLocaleString('fr-BE', { maximumFractionDigits: decimals });

const fmtDate = (iso: string | null) => (iso ? new Date(`${iso.slice(0, 10)}T00:00:00`).toLocaleDateString('fr-BE') : '');

/** Remplace les variables d'un modèle. Les valeurs sont échappées dans le corps (HTML). */
export function renderTemplate(
  tpl: Pick<MessageTemplate, 'subject' | 'body'>,
  c: CrmContact,
  operations: SharingOperation[],
  /** Valeurs calculées ailleurs (ex. estimation des économies à l'envoi du contrat). */
  extra: Record<string, string> = {},
): { subject: string; body: string } {
  const eans = c.meters.map(m => m.ean_code).filter(Boolean);
  const values: Record<string, string> = {
    '{{nom}}': displayName(c),
    '{{prenom}}': c.first_name ?? '',
    '{{nom_famille}}': c.last_name ?? '',
    '{{societe}}': c.entity_type === 'morale' ? c.company_name ?? '' : '',
    '{{email}}': c.email ?? '',
    '{{telephone}}': c.phone ?? '',
    '{{adresse}}': c.domicile_address ?? '',
    '{{email_facturation}}': c.billing_email ?? '',
    '{{adresse_facturation}}': c.billing_address ?? '',
    '{{forme_juridique}}': c.entity_type === 'morale' ? c.legal_form ?? '' : '',
    '{{n_entreprise}}': c.entity_type === 'morale' ? c.company_number ?? '' : '',
    '{{qualite}}': c.entity_type === 'morale' ? c.representative_title ?? '' : '',
    '{{ean}}': eans[0] ?? '',
    '{{liste_ean}}': eans.join(', '),
    '{{date_debut}}': fmtDate(c.sharing_start_date),
    '{{prix}}': fmtNumber(c.sharing_price_cents_kwh ?? c.price_cents_kwh, 4),
    '{{prix_vente}}': fmtNumber(c.sharing_sell_price_cents_kwh ?? c.sell_price_cents_kwh, 4),
    '{{cotisation}}': fmtNumber(c.annual_fee, 2),
    '{{operation}}': operations.find(o => o.id === c.sharing_operation_id)?.name ?? '',
    '{{reference_mandat}}': c.mandate_reference ?? '',
    ...savingsVariables(null),
    ...extra,
  };
  const apply = (text: string, escape: boolean) =>
    Object.entries(values).reduce((acc, [k, v]) => acc.split(k).join(escape ? escapeHtml(v) : v), text);
  return { subject: apply(tpl.subject, false), body: apply(tpl.body, true) };
}

/** Envoie le contrat PDF avec le message « Envoi du contrat ». */
/**
 * Envoie la convention pré-remplie (avec le mandat SEPA si `sepa`).
 * Renvoie les avertissements de generate-contract et la référence du mandat attribuée.
 */
/** Contenu d'un e-mail (objet et corps), tel qu'il partira. Le corps peut contenir des variables {{…}} pas encore connues. */
export interface EmailContent { subject: string; body: string }

/**
 * Objet et corps de l'e-mail du contrat. `content` : texte déjà relu (et éventuellement modifié) dans l'aperçu ;
 * ses variables restantes sont remplacées, la phrase d'estimation n'est pas rajoutée.
 */
async function buildContractEmail(
  c: CrmContact, operations: SharingOperation[], mandateReference: string | null, content?: EmailContent, pendingReference = false,
): Promise<{ subject: string; body: string; estimate: SavingsEstimate | null }> {
  const tpl = content ?? await getTemplate('contract');
  // Estimation des économies demandée dans le formulaire (réglage activé) : variable du message, sinon phrase ajoutée.
  let estimate: SavingsEstimate | null = null;
  let sentence = '';
  try {
    const settings = await fetchSavingsSettings();
    estimate = settings.enabled ? contractEstimate(c, settings) : null;
    if (!content && estimate && !tpl.body.includes('{{economie_estimee}}')) sentence = renderSavingsSentence(settings.sentence, estimate);
  } catch (err) {
    console.warn('Estimation des économies non calculée :', errorMessage(err));
  }
  // Référence du mandat pas encore attribuée (aperçu) : la variable reste visible et sera remplie à l'envoi.
  const extra = { ...savingsVariables(estimate), ...(pendingReference ? { '{{reference_mandat}}': '{{reference_mandat}}' } : {}) };
  const { subject, body } = renderTemplate(tpl, { ...c, mandate_reference: mandateReference }, operations, extra);
  return { subject, body: sentence ? insertBeforeSignature(body, escapeHtml(sentence)) : body, estimate };
}

/** Aperçu de l'e-mail du contrat avant envoi (aucun document généré, aucune référence attribuée). */
export async function previewContractEmail(c: CrmContact, operations: SharingOperation[], opts: { sepa?: boolean } = {}): Promise<EmailContent> {
  const reference = c.mandate_reference && c.mandate_status !== 'revoked' ? c.mandate_reference : null;
  const { subject, body } = opts.sepa
    ? await buildContractEmail(c, operations, reference, undefined, !reference)
    : await buildContractEmail(c, operations, c.mandate_reference);
  return { subject, body };
}

/** Aperçu d'un message envoyé au passage d'étape : accueil, invitation, ou message libre du déclencheur. */
export async function previewMessage(
  c: CrmContact, operations: SharingOperation[], source: 'welcome' | 'invitation' | EmailContent,
): Promise<EmailContent> {
  const tpl = typeof source === 'string' ? await getTemplate(source) : source;
  return renderTemplate(tpl, c, operations);
}

export async function sendContractEmail(
  c: CrmContact, operations: SharingOperation[], opts: { sepa?: boolean; content?: EmailContent } = {},
): Promise<{ warnings: string[]; mandateReference: string | null; estimate: SavingsEstimate | null }> {
  if (!c.email) throw new Error("Le contact n'a pas d'adresse e-mail");
  const doc = await generateContract(c.id, { sepa: opts.sepa, assignReference: opts.sepa });
  const mandateReference = doc.mandate_reference ?? null;
  const { subject, body, estimate } = await buildContractEmail(c, operations, mandateReference ?? c.mandate_reference, opts.content);
  await sendMemberEmail({ name: displayName(c), email: c.email, subject, message: body, attachmentBase64: doc.pdfBase64, attachmentFilename: doc.fileName });
  return { warnings: doc.warnings ?? [], mandateReference, estimate };
}

/** Ajoute un paragraphe avant la formule de politesse (« Bien cordialement… »), sinon à la fin. */
export function insertBeforeSignature(body: string, paragraph: string): string {
  const m = /\n\s*(bien cordialement|cordialement|bien à vous|meilleures salutations|salutations)/i.exec(body);
  if (!m) return `${body.trimEnd()}\n\n${paragraph}`;
  const before = body.slice(0, m.index).trimEnd();
  return `${before}\n\n${paragraph}\n\n${body.slice(m.index).replace(/^\s+/, '')}`;
}

/** Envoie le mandat SEPA seul (membre déjà actif) avec le message « Mandat SEPA ». Renvoie la référence attribuée. */
export async function sendMandateEmail(c: CrmContact, operations: SharingOperation[]): Promise<string> {
  if (!c.email) throw new Error("Le contact n'a pas d'adresse e-mail");
  const [doc, tpl] = await Promise.all([generateContract(c.id, { document: 'mandate', assignReference: true }), getTemplate('mandate')]);
  const reference = doc.mandate_reference;
  if (!reference) throw new Error('Référence du mandat non attribuée');
  const { subject, body } = renderTemplate(tpl, { ...c, mandate_reference: reference }, operations);
  await sendMemberEmail({ name: displayName(c), email: c.email, subject, message: body, attachmentBase64: doc.pdfBase64, attachmentFilename: doc.fileName });
  return reference;
}

/** Invitation à créer un compte sur la plateforme (message « Invitation à la plateforme »). */
export async function sendInvitation(c: CrmContact, operations: SharingOperation[], content?: EmailContent): Promise<void> {
  if (!c.email) throw new Error("Le contact n'a pas d'adresse e-mail");
  if (!c.meters.some(m => m.participant_id)) throw new Error("Aucune fiche participant : le membre ne pourrait pas s'inscrire avec son EAN");
  const { subject, body } = renderTemplate(content ?? await getTemplate('invitation'), c, operations);
  await sendMemberEmail({ name: displayName(c), email: c.email, subject, message: body });
}

/** Message libre (déclencheur d'étape) : variables du CRM remplacées, envoyé depuis info@sunisup.be. */
export async function sendCustomMessage(c: CrmContact, operations: SharingOperation[], tpl: { subject: string; body: string }): Promise<void> {
  if (!c.email) throw new Error("Le contact n'a pas d'adresse e-mail");
  const { subject, body } = renderTemplate(tpl, c, operations);
  await sendMemberEmail({ name: displayName(c), email: c.email, subject, message: body });
}

/** E-mail d'accueil (message « Message de bienvenue »). */
export async function sendWelcome(c: CrmContact, operations: SharingOperation[], content?: EmailContent): Promise<void> {
  if (!c.email) throw new Error("Le contact n'a pas d'adresse e-mail");
  const { subject, body } = renderTemplate(content ?? await getTemplate('welcome'), c, operations);
  await sendMemberEmail({ name: displayName(c), email: c.email, subject, message: body });
}

/** Lien vers le formulaire « nous-rejoindre », avec le message « Demande d'informations ». */
/**
 * Envoie le lien vers le formulaire d'adhésion (pré-rempli avec l'e-mail du contact) et note la date d'envoi.
 * Quand la personne remplit le formulaire avec cette adresse, le lead est complété automatiquement.
 */
export async function sendQuestionnaire(c: CrmContact, operations: SharingOperation[]): Promise<CrmContact> {
  if (!c.email) throw new Error("Le contact n'a pas d'adresse e-mail");
  const tpl = await getTemplate('questionnaire');
  const rendered = renderTemplate(tpl, c, operations);
  const res = await callFunction<{ successCount?: number; results?: { success: boolean; error?: string }[] }>('send-questionnaire', {
    emails: [c.email],
    subject: rendered.subject,
    message: rendered.body,
    app_url: window.location.origin,
  });
  if (!res.successCount) throw new Error(res.results?.[0]?.error ?? "Le formulaire n'a pas pu être envoyé");
  // Formulaire envoyé : on attend la réponse du client.
  const now = new Date().toISOString();
  return updateContact(c.id, { questionnaire_sent_at: now, waiting_on: 'client', waiting_since: now });
}
